// src/utils/gamification.js
// ─────────────────────────────────────────────
// Level and badge logic for WasteGame carrier gamification.
// ─────────────────────────────────────────────

// ─── LEVELS ──────────────────────────────────────────────────────────────────

export const LEVELS = [
  { id: 'rookie',  label: 'Rookie',  emoji: '🌱', minJobs: 0   },
  { id: 'hustler', label: 'Hustler', emoji: '⚡', minJobs: 11  },
  { id: 'pro',     label: 'Pro',     emoji: '🔥', minJobs: 26  },
  { id: 'elite',   label: 'Elite',   emoji: '💎', minJobs: 51  },
  { id: 'legend',  label: 'Legend',  emoji: '👑', minJobs: 101 },
];

export const getLevel = (jobs = 0) => {
  let current = LEVELS[0];
  for (const lvl of LEVELS) {
    if (jobs >= lvl.minJobs) current = lvl;
  }
  return current;
};

export const getLevelProgress = (jobs = 0) => {
  const level     = getLevel(jobs);
  const levelIdx  = LEVELS.indexOf(level);
  const isMax     = levelIdx === LEVELS.length - 1;

  if (isMax) {
    return { level, nextLevel: null, progress: 100, jobsNeeded: 0 };
  }

  const nextLevel      = LEVELS[levelIdx + 1];
  const rangeSize      = nextLevel.minJobs - level.minJobs;
  const jobsIntoLevel  = jobs - level.minJobs;
  const progress       = Math.min(100, Math.round((jobsIntoLevel / rangeSize) * 100));
  const jobsNeeded     = nextLevel.minJobs - jobs;

  return { level, nextLevel, progress, jobsIntoLevel, jobsNeeded };
};

// ─── BADGES ──────────────────────────────────────────────────────────────────

export const BADGES = [
  {
    id:    'verified_pro',
    emoji: '✅',
    label: 'Register checked',
    desc:  'Waste carrier registration checked against the public register before approval',
    check: (p) => !!p.verified,
  },
  {
    id:    'eco_warrior',
    emoji: '♻️',
    label: 'Eco Warrior',
    desc:  'Complete 25 jobs without any complaints',
    check: (p) => (p.jobs || 0) >= 25 && (p.complaintsReceived || 0) === 0,
  },
  {
    id:    'speed_demon',
    emoji: '🚀',
    label: 'Speed Demon',
    desc:  'First bid on 10 jobs',
    check: (p) => (p.firstBids || 0) >= 10,
  },
  {
    id:    'five_star',
    emoji: '⭐',
    label: 'Five Star',
    desc:  'Earn 10 five-star reviews',
    check: (p) => (p.fiveStarReviews || 0) >= 10,
  },
  {
    id:    'champion',
    emoji: '🏆',
    label: 'Champion',
    desc:  'Win the weekly Yard league',
    check: (p) => (p.leagueWins || 0) >= 1,
  },
  {
    id:    'century',
    emoji: '💯',
    label: 'Century',
    desc:  'Complete 100 jobs',
    check: (p) => (p.jobs || 0) >= 100,
  },
  {
    id:    'green_machine',
    emoji: '🌿',
    label: 'Green Machine',
    desc:  'Earn 500 Eco Credits',
    check: (p) => (p.credits || 0) >= 500,
  },
  {
    id:    'sharp_shooter',
    emoji: '🎯',
    label: 'Sharp Shooter',
    desc:  '50%+ bid win rate (min 20 bids)',
    check: (p) => {
      const total = p.totalBids || 0;
      const won   = p.jobs     || 0;
      return total >= 20 && won / total >= 0.5;
    },
  },
  {
    id:    'early_adopter',
    emoji: '🎖️',
    label: 'Early Adopter',
    desc:  'One of the first 100 carriers on WasteGame',
    check: (p) => !!p.earlyAdopter,
  },
];

/** Returns every badge with an `earned` boolean added. */
export const getEarnedBadges = (provider = {}) =>
  BADGES.map(badge => ({ ...badge, earned: badge.check(provider) }));

/** Returns badges newly unlocked since `prevProvider` became `nextProvider`. */
export const getNewBadges = (prevProvider = {}, nextProvider = {}) =>
  BADGES.filter(badge => !badge.check(prevProvider) && badge.check(nextProvider));
